import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Audience, AudienceFilterApp } from '../../audience/components/audience.model';
import { AudienceReachService } from '../../audience/audience-reach.service';
import { AndOrOperator } from '../../campaign/widgets.model';
import { DataPlatformService } from '../data-platform.service';
import { DataPlatformAudienceRule, DataPlatformBreakdownRow, DataPlatformEventFilter } from '../data-platform.model';
import { localDay } from '../../shared/kpi-grid/kpi-trend-math';
import {
  AudienceReachCategory,
  categoriseAudience,
  computeGuesstimate,
  computeNotInListGuesstimate,
  extractThirdPartySource,
} from './audience-guesstimate.utils';
import { rulesToBreakdownFilter } from './audience-filter-to-dp-rule.utils';
import { defaultAudienceRule } from './audience-filter-to-rule.utils';
import { audienceCountRules } from './comparable-rules.utils';
import {
  CampaignWidgetResolver,
  EstimateFactor,
  FactorDimension,
  applyFactors,
  combinedShare,
  factorTopN,
  mapCriteria,
  matchedCount,
  matchesEitherSide,
  passRate,
} from './reach-estimate-factors.utils';
import { ReachPool, estimateGroup, reachPools } from './unique-reach.utils';

const IDENTIFIED_RULES: DataPlatformAudienceRule[] = [{ type: 'is_identified' }];

/** 'na' (not available) is shown as N/A. */
export type DpReachKind = 'exact' | 'estimate' | 'upper-bound' | 'lower-bound' | 'na' | 'loading';

/** One id for every reach count, so DataPlatform's response cache matches equal rules across callers. */
export const REACH_COUNT_ID = 'reach-internal';

const DAY_MS = 24 * 60 * 60 * 1000;
const REACH_FALLBACK_WINDOW_MS = 90 * DAY_MS;

/** The page period, or a trailing window until the filter bar has emitted one. */
export function reachBaseFilter(
  pageFilter: DataPlatformEventFilter | undefined,
  siteId: number,
): DataPlatformEventFilter {
  // The page filter can still name the site from before a switch.
  return pageFilter
    ? { ...pageFilter, siteIds: pageFilter.siteIds?.includes(siteId) ? pageFilter.siteIds : [siteId] }
    : { siteIds: [siteId], from: new Date(Date.now() - REACH_FALLBACK_WINDOW_MS).toISOString() };
}

export interface ReachFactorNote {
  dimension: FactorDimension;
  /** Human label for the criterion (e.g. "desktop", "SE", "email"). */
  label: string;
  /** Translation key for a generic label; see EstimateFactor.labelKey. */
  labelKey?: string;
  /** The share is of everyone outside the label. */
  negate?: boolean;
  /** Applied pass-rate, 0..1 (after any negation). */
  share: number;
}

export interface DpReachResult {
  kind: DpReachKind;
  category: AudienceReachCategory;
  /** Headline number: centre for estimate, exact value for exact, ceiling for upper-bound, floor for lower-bound. */
  value: number | null;
  /** Plausible spread (already factor-scaled). Null unless we have a real range. */
  range: { lower: number; upper: number } | null;
  isEstimate: boolean;
  hasConditions: boolean;
  /** Estimable criteria we applied, for the explanation note. */
  factorNotes?: ReachFactorNote[];
  /** Share of the counted base the factors keep, 0..1. */
  factorShare?: number;
  /** Criteria we could not size — the reason the value is a "≤" bound. */
  unquantifiable?: string[];
  /** Internal/list split for mixed 3rd-party audiences. */
  breakdown?: { internalCount: number; listTotal: number | null; app?: AudienceFilterApp } | null;
  /** Set when the value counts visitors in this period, not profiles matching now. */
  basePeriod?: { start: Date; end: Date };
  pools?: ReachPool[];
}

interface BaseReach {
  value: number | null;
  range: { lower: number; upper: number } | null;
  breakdown?: { internalCount: number; listTotal: number | null; app?: AudienceFilterApp } | null;
  /** mixedSupported where the list total couldn't be fetched → treat as a floor. */
  degrade?: boolean;
  /** No countable rules — the base population must be derived from the first
   *  estimable factor's absolute bucket count (the backend rejects empty-rule
   *  counts). e.g. a "visited from Facebook"-only audience. */
  populationFromFactors?: boolean;
}

/**
 * Single source of truth for resolving an audience's reach on the new
 * DataPlatform Audiences page. Both the hub and the flyout consume this so they
 * agree. Combines:
 *  - the countable base (DP `countAudience`, incl. promoted rules),
 *  - the 3rd-party list total `L` (legacy `getIntegrationsReach`),
 *  - the total-identified denominator `T` (cached per site),
 * into the real `computeGuesstimate`, then refines it by the estimable criteria
 * (device/country/source/utm) via `getBreakdown` shares.
 */
@Injectable({ providedIn: 'root' })
export class DataPlatformAudienceReachService {
  private readonly dp = inject(DataPlatformService);
  private readonly legacyReach = inject(AudienceReachService);
  private readonly totalIdentifiedCache = new Map<number, Observable<number>>();

  /**
   * True when the reach is more than a single count: estimable factors, promoted
   * rules or a 3rd-party list estimate, so it depends on the period, the widgets or the list.
   */
  needsResolution(audience: Audience, resolveWidgetId?: CampaignWidgetResolver): boolean {
    const { factors, promotedRules } = mapCriteria(audience, resolveWidgetId);
    const category = categoriseAudience(audience);
    return (
      matchesEitherSide(audience) ||
      factors.length > 0 ||
      promotedRules.length > 0 ||
      category === 'mixedSupported' ||
      category === 'mixedUnsupported' ||
      !!extractThirdPartySource(audience)?.negated
    );
  }

  resolveReach(
    audience: Audience,
    baseFilter: DataPlatformEventFilter,
    resolveWidgetId?: CampaignWidgetResolver,
    featurePage = 'audiences',
  ): Observable<DpReachResult> {
    if (matchesEitherSide(audience)) {
      return this.resolveEitherReach(audience, baseFilter, resolveWidgetId, featurePage);
    }

    const siteId = baseFilter.siteIds[0];
    const category = categoriseAudience(audience);
    const hasConditions = (audience.sessionConditions?.length ?? 0) > 0;

    if (category === 'unsupported') {
      return of(unavailableReach(category, hasConditions));
    }

    if (audience.isDefault && !audience.filters?.length) {
      return this.countDefault(audience, siteId, category, hasConditions, featurePage);
    }

    const counted = audienceCountRules(audience);
    if (!counted.countable) {
      return of(unavailableReach(category, hasConditions));
    }

    const { promotedRules, factors, unquantifiable } = mapCriteria(audience, resolveWidgetId);
    const internalRules = [...counted.rules, ...promotedRules];
    const source = extractThirdPartySource(audience);
    const app = source?.app;
    const poolRules = basePoolRules(category, internalRules, !!source?.negated);
    const group = estimateGroup(factors, source);

    // mixedUnsupported: internal rules AND a 3rd-party list (conjunctive), but
    // the list can't be sized on the frontend. The internal-rules count is a
    // CEILING (the list only narrows it further) → show "≤", not a floor. With
    // no internal rules at all there's nothing to bound → N/A.
    if (category === 'mixedUnsupported') {
      if (internalRules.length === 0) return of(unavailableReach(category, hasConditions));
      return this.countInternal(siteId, internalRules, featurePage).pipe(
        map((i) =>
          i == null
            ? unavailableReach(category, hasConditions)
            : {
                kind: 'upper-bound' as const,
                category,
                value: i,
                range: null,
                isEstimate: true,
                hasConditions,
                breakdown: { internalCount: i, listTotal: null, app },
                unquantifiable: ['thirdParty', ...unquantifiable],
                pools: reachPools({ kind: 'upper-bound', value: i, range: null }, internalRules, group, false),
              },
        ),
      );
    }

    const base$ = source?.negated
      ? this.resolveNotInList(audience, siteId, internalRules, app, featurePage)
      : this.resolveBase(category, audience, siteId, internalRules, app, featurePage);
    const scopedFilter = rulesToBreakdownFilter(internalRules, baseFilter) ?? baseFilter;
    const breakdowns$ = this.fetchFactorBreakdowns(factors, scopedFilter, featurePage);

    return forkJoin([base$, breakdowns$]).pipe(
      map(([base, byDim]) => {
        const combined = this.combine(base, byDim, factors, unquantifiable, category, hasConditions);
        const wholePool = category === 'internal' && internalRules.length > 0;
        const result = { ...combined, pools: reachPools(combined, poolRules, group, wholePool) };
        return base.populationFromFactors && result.value != null
          ? { ...result, basePeriod: periodOf(baseFilter) }
          : result;
      }),
    );
  }

  /** All profiles, counted on the same basis as every other audience. */
  countEveryone(siteId: number, featurePage: string): Observable<DpReachResult> {
    return this.countAllProfiles(siteId, featurePage).pipe(
      map((everyone) =>
        everyone == null
          ? unavailableReach('internal', false)
          : {
              kind: 'exact' as const,
              category: 'internal' as const,
              value: everyone,
              range: null,
              isEstimate: false,
              hasConditions: false,
            },
      ),
    );
  }

  private resolveEitherReach(
    audience: Audience,
    baseFilter: DataPlatformEventFilter,
    resolveWidgetId: CampaignWidgetResolver | undefined,
    featurePage: string,
  ): Observable<DpReachResult> {
    const both: Audience = { ...audience, operator: AndOrOperator.And };
    return forkJoin([
      this.resolveReach({ ...both, sessionConditions: [] }, baseFilter, resolveWidgetId, featurePage),
      this.resolveReach({ ...both, filters: [] }, baseFilter, resolveWidgetId, featurePage),
      this.resolveReach(both, baseFilter, resolveWidgetId, featurePage),
    ]).pipe(map(([profile, visit, overlap]) => eitherReach(profile, visit, overlap, categoriseAudience(audience))));
  }

  /** Built-in audiences carry no filters; their name says what to count. */
  private countDefault(
    audience: Audience,
    siteId: number,
    category: AudienceReachCategory,
    hasConditions: boolean,
    featurePage: string,
  ): Observable<DpReachResult> {
    const rule = defaultAudienceRule(audience.name);
    if (!rule) {
      return of(unavailableReach(category, hasConditions));
    }
    return this.countInternal(siteId, [rule], featurePage).pipe(
      map((count) =>
        count == null
          ? unavailableReach(category, hasConditions)
          : { kind: 'exact' as const, category, value: count, range: null, isEstimate: false, hasConditions },
      ),
    );
  }

  // ---- base reach per category ----

  private resolveBase(
    category: AudienceReachCategory,
    audience: Audience,
    siteId: number,
    internalRules: DataPlatformAudienceRule[],
    app: AudienceFilterApp | undefined,
    featurePage: string,
  ): Observable<BaseReach> {
    if (category === 'thirdPartyOnly') {
      return this.listTotal(audience).pipe(map((listTotal) => ({ value: listTotal, range: null })));
    }
    if (category === 'mixedSupported') {
      // No countable internal rule (e.g. Country=SE + a list) → the internal
      // match is the whole identified population, so the centre is the list
      // total; any factors narrow it afterwards.
      const internal$ = internalRules.length
        ? this.countInternal(siteId, internalRules, featurePage)
        : of<number | null>(null);
      return forkJoin([this.listTotal(audience), internal$, this.totalIdentified(siteId, featurePage)]).pipe(
        map(([listTotal, internalCount, totalIdentified]) => {
          const effectiveInternalCount = internalRules.length ? internalCount : totalIdentified;
          if (listTotal == null || effectiveInternalCount == null) {
            // Can't size the list → fall back to a floor on the internal count.
            return {
              value: effectiveInternalCount,
              range: null,
              breakdown: { internalCount: effectiveInternalCount ?? 0, listTotal: null, app },
              degrade: true,
            };
          }
          const guesstimate = computeGuesstimate({
            listTotal,
            internalCount: effectiveInternalCount,
            totalIdentified,
          });
          return {
            value: guesstimate.centre,
            range: { lower: guesstimate.lower, upper: guesstimate.upper },
            breakdown: { internalCount: effectiveInternalCount, listTotal, app },
          };
        }),
      );
    }
    // internal — with no countable rule the base comes from the factor breakdown
    // (the matched bucket's absolute count), resolved in combine().
    if (internalRules.length === 0) {
      return of<BaseReach>({ value: null, range: null, populationFromFactors: true });
    }
    return this.countInternal(siteId, internalRules, featurePage).pipe(
      map((internalCount) => ({ value: internalCount, range: null })),
    );
  }

  /** Without a countable rule the base is every profile: the list check matches any visitor it cannot identify. */
  private resolveNotInList(
    audience: Audience,
    siteId: number,
    internalRules: DataPlatformAudienceRule[],
    app: AudienceFilterApp | undefined,
    featurePage: string,
  ): Observable<BaseReach> {
    const hasRules = internalRules.length > 0;
    const base$ = hasRules
      ? this.countInternal(siteId, internalRules, featurePage)
      : this.countAllProfiles(siteId, featurePage);
    return forkJoin([this.listTotal(audience), base$, this.totalIdentified(siteId, featurePage)]).pipe(
      map(([listTotal, baseCount, totalIdentified]) => {
        if (baseCount == null) {
          return { value: null, range: null };
        }
        if (listTotal == null) {
          return {
            value: baseCount,
            range: null,
            breakdown: { internalCount: baseCount, listTotal: null, app },
            degrade: true,
          };
        }
        const guesstimate = computeNotInListGuesstimate(baseCount, {
          listTotal,
          internalCount: hasRules ? baseCount : totalIdentified,
          totalIdentified,
        });
        return {
          value: guesstimate.centre,
          range: { lower: guesstimate.lower, upper: guesstimate.upper },
          breakdown: { internalCount: baseCount, listTotal, app },
        };
      }),
    );
  }

  // ---- factor breakdowns ----

  private fetchFactorBreakdowns(
    factors: EstimateFactor[],
    scopedFilter: DataPlatformEventFilter,
    featurePage: string,
  ): Observable<Record<string, DataPlatformBreakdownRow[] | null>> {
    const dims = Array.from(new Set(factors.map((f) => f.dimension)));
    if (dims.length === 0) return of({});
    return forkJoin(
      // `null` = the breakdown call failed (can't size); `[]` = it succeeded but
      // the dimension had no traffic (a genuine zero, not an error).
      dims.map((dim) =>
        this.dp
          .getBreakdown({ filter: scopedFilter, dimension: dim, ...factorTopN(dim) }, `${featurePage}-reach-factor`)
          .pipe(
            map((r) => ({ dim, rows: r?.isPositive() && r.data ? r.data.rows : null })),
            catchError(() => of({ dim, rows: null as DataPlatformBreakdownRow[] | null })),
          ),
      ),
    ).pipe(
      map((entries) => {
        const rec: Record<string, DataPlatformBreakdownRow[] | null> = {};
        for (const e of entries) rec[e.dim] = e.rows;
        return rec;
      }),
    );
  }

  private combine(
    base: BaseReach,
    byDim: Record<string, DataPlatformBreakdownRow[] | null>,
    factors: EstimateFactor[],
    unquantifiable: string[],
    category: AudienceReachCategory,
    hasConditions: boolean,
  ): DpReachResult {
    // List total couldn't be fetched for a mixed audience → the internal count
    // is a ceiling (the list only narrows it), so show "≤", not a floor.
    if (base.degrade) {
      if (base.value == null) return unavailableReach(category, hasConditions);
      return {
        kind: 'upper-bound',
        category,
        value: base.value,
        range: null,
        isEstimate: true,
        hasConditions,
        breakdown: base.breakdown ?? null,
        unquantifiable: ['thirdParty', ...unquantifiable],
      };
    }

    const rates: number[] = [];
    const notes: ReachFactorNote[] = [];
    const noData: string[] = [];
    // For a condition-only audience the base population isn't countable (the
    // backend rejects empty rules) — the first estimable factor's absolute
    // matched count becomes the base; the rest narrow it as ratios.
    let baseValue = base.value;
    let populationConsumed = false;
    for (const factor of factors) {
      const rows = byDim[factor.dimension];
      const rate = passRate(rows, factor);
      const note: ReachFactorNote = {
        dimension: factor.dimension,
        label: factor.label,
        labelKey: factor.labelKey,
        negate: factor.negate,
        share: rate,
      };
      if (base.populationFromFactors && !populationConsumed) {
        const matchedTotal = matchedCount(rows, factor);
        if (!Number.isFinite(matchedTotal)) {
          noData.push(factor.label);
          continue;
        }
        baseValue = matchedTotal;
        populationConsumed = true;
        if (Number.isFinite(rate)) {
          notes.push(note);
        }
        continue;
      }
      if (Number.isFinite(rate)) {
        rates.push(rate);
        notes.push(note);
      } else {
        noData.push(factor.label);
      }
    }

    if (baseValue == null) return unavailableReach(category, hasConditions);

    const value = applyFactors(baseValue, rates);
    const range = base.range
      ? { lower: applyFactors(base.range.lower, rates), upper: applyFactors(base.range.upper, rates) }
      : null;
    const allUnquant = [...unquantifiable, ...noData];

    let kind: DpReachKind = category === 'mixedSupported' || base.range ? 'estimate' : 'exact';
    if ((rates.length > 0 || base.populationFromFactors) && kind === 'exact') kind = 'estimate';
    if (allUnquant.length > 0) kind = 'upper-bound';

    const isEstimate =
      kind === 'estimate' || category === 'mixedSupported' || rates.length > 0 || !!base.populationFromFactors;

    return {
      kind,
      category,
      value,
      range,
      isEstimate,
      hasConditions,
      factorNotes: notes.length ? notes : undefined,
      factorShare:
        kind === 'estimate' && rates.length > 0 && !base.populationFromFactors ? combinedShare(rates) : undefined,
      unquantifiable: allUnquant.length ? allUnquant : undefined,
      breakdown: base.breakdown ?? null,
    };
  }

  // ---- input fetchers ----

  /**
   * I — base count of the audience's countable rules (+ promoted rules).
   * Empty rules are guarded (the backend 400s on them); condition-only audiences
   * derive their base from the factor breakdown in combine() instead.
   */
  private countInternal(
    siteId: number,
    rules: DataPlatformAudienceRule[],
    featurePage: string,
  ): Observable<number | null> {
    if (rules.length === 0) return of(null);
    return this.dp
      .countAudience({ siteIds: [siteId], audience: { id: REACH_COUNT_ID, rules } }, `${featurePage}-reach-internal`)
      .pipe(
        map((r) => (r?.isPositive() && r.data ? (r.data.count ?? 0) : null)),
        catchError(() => of(null)),
      );
  }

  private countAllProfiles(siteId: number, featurePage: string): Observable<number | null> {
    return forkJoin([
      this.countInternal(siteId, [{ type: 'is_identified' }], featurePage),
      this.countInternal(siteId, [{ type: 'is_anonymous' }], featurePage),
    ]).pipe(
      map(([identified, anonymous]) => (identified == null || anonymous == null ? null : identified + anonymous)),
    );
  }

  /** L — 3rd-party list total via the legacy integration endpoint. */
  private listTotal(audience: Audience): Observable<number | null> {
    const src = extractThirdPartySource(audience);
    if (!src) return of(null);
    return this.legacyReach.getIntegrationsReach(src.app, src.listId).pipe(
      map((n) => (typeof n === 'number' ? n : null)),
      catchError(() => of(null)),
    );
  }

  /** T — total identified visitors. Cached per site so a list fires one request. */
  private totalIdentified(siteId: number, featurePage: string): Observable<number> {
    let cached = this.totalIdentifiedCache.get(siteId);
    if (!cached) {
      cached = this.dp
        .countAudience(
          { siteIds: [siteId], audience: { id: 'reach-total-identified', rules: [{ type: 'is_identified' }] } },
          `${featurePage}-reach-total`,
        )
        .pipe(
          map((r) => (r?.isPositive() && r.data ? (r.data.count ?? 0) : 0)),
          catchError(() => of(0)),
          shareReplay(1),
        );
      this.totalIdentifiedCache.set(siteId, cached);
    }
    return cached;
  }
}

/** The result when an audience's reach cannot be counted. */
export function unavailableReach(category: AudienceReachCategory, hasConditions: boolean): DpReachResult {
  return { kind: 'na', category, value: null, range: null, isEstimate: false, hasConditions };
}

export function reachPeriodLabel(
  period: { start: Date; end: Date } | undefined,
): { key: string; params: Record<string, string | number> } | null {
  if (!period) {
    return null;
  }
  if (Date.now() - period.end.getTime() > DAY_MS) {
    return { key: 'data-platform.reach-period', params: { from: localDay(period.start), to: localDay(period.end) } };
  }
  const days = Math.round((period.end.getTime() - period.start.getTime()) / DAY_MS);
  return days > 1
    ? { key: 'data-platform.reach-last-days', params: { days } }
    : { key: 'data-platform.reach-last-day', params: {} };
}

/** Null for everyone on the site. */
function basePoolRules(
  category: AudienceReachCategory,
  internalRules: DataPlatformAudienceRule[],
  notInList: boolean,
): DataPlatformAudienceRule[] | null {
  if (internalRules.length > 0) {
    return internalRules;
  }
  return !notInList && (category === 'thirdPartyOnly' || category === 'mixedSupported') ? IDENTIFIED_RULES : null;
}

function isSized(result: DpReachResult): boolean {
  return result.value != null && (result.kind === 'exact' || result.kind === 'estimate');
}

function eitherReach(
  profile: DpReachResult,
  visit: DpReachResult,
  overlap: DpReachResult,
  category: AudienceReachCategory,
): DpReachResult {
  const sized = [profile, visit].filter(isSized);
  if (sized.length === 0) {
    return unavailableReach(category, true);
  }
  const notes = [...(profile.factorNotes ?? []), ...(visit.factorNotes ?? [])];
  // The visit side counts visitors in the period; label it only when it is the whole number.
  const basePeriod = !isSized(profile) || profile.value === 0 ? visit.basePeriod : undefined;
  const pools = sized.every((r) => r.pools) ? sized.flatMap((r) => r.pools ?? []) : undefined;
  const common = {
    category,
    range: null,
    hasConditions: true,
    factorNotes: notes.length ? notes : undefined,
    breakdown: profile.breakdown ?? null,
    ...(basePeriod && { basePeriod }),
    ...(pools && { pools }),
  };
  if (sized.length === 1 || !isSized(overlap)) {
    return { ...common, kind: 'lower-bound', value: Math.max(...sized.map((r) => r.value!)), isEstimate: true };
  }
  const exact = [profile, visit, overlap].every((r) => r.kind === 'exact');
  const overlapCount = Math.min(overlap.value!, profile.value!, visit.value!);
  return {
    ...common,
    kind: exact ? 'exact' : 'estimate',
    value: profile.value! + visit.value! - overlapCount,
    isEstimate: !exact,
  };
}

function periodOf(filter: DataPlatformEventFilter): { start: Date; end: Date } {
  return { start: new Date(filter.from), end: filter.to ? new Date(filter.to) : new Date() };
}
